import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Users, Target, Award, Clock } from 'lucide-react';

const stats = [
  { icon: Users, value: '50+', label: 'Projects Delivered' },
  { icon: Target, value: '£1.5M+', label: 'Systems Value Built' },
  { icon: Award, value: '14+', label: 'Years Engineering Experience' },
  { icon: Clock, value: '99.9%', label: 'Uptime Guarantee' }
];

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
              About LOLCorp
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#003333] mb-8">
              Engineering Excellence Since 2024
            </h2>
            
            <div className="space-y-6 text-lg leading-relaxed text-slate-600">
              <p>
                LOLCorp PVT LTD specializes in building custom internal systems that transform how businesses operate. We replace spreadsheets, fragile workflows, and disconnected tools with secure, scalable software built around how your business actually works.
              </p>
              <p>
                Our team of senior engineers brings over 14 years of combined experience across industries including finance, healthcare, logistics, and retail. We understand that every business is unique, which is why we never offer one-size-fits-all solutions.
              </p>
              <p>
                From project management tools to inventory systems, sales dashboards to internal communication platforms – we build the software that powers your daily operations.
              </p>
            </div>
          </motion.div>
          
          {/* Right Content - Stats */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 gap-6"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="bg-white p-6 md:p-8 border border-slate-100 hover:border-[#003333]/20 transition-all duration-500 card-hover"
                  data-testid={`about-stat-${index}`}
                >
                  <div className="w-12 h-12 bg-[#F8FAFC] rounded-xl flex items-center justify-center mb-4 text-[#003333]">
                    <Icon className="w-6 h-6" />
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-[#003333] mb-2">
                    {stat.value}
                  </p>
                  <p className="text-slate-600 text-sm">
                    {stat.label}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
